import { useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Crown, Check } from "lucide-react";
import { StripeLogo, CashfreeLogo, PaymentProcessorsBadge } from "@/components/icons/PaymentLogos";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { useAuth } from "@/hooks/use-auth";
import { IntlPhoneInput } from "@/components/ui/intl-phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { applyGst, formatInr } from "@/lib/gst";

const FOUNDING_INR_BASE = 39999;

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const PERKS = [
  "Name listed on the public Founding Members wall",
  "Permanent Founding Member badge (yours for life)",
  "Priority enrollment for every future masterclass",
  "Custom AI workflow requests, quarterly",
  "30-minute private strategy call, quarterly",
  "Quarterly behind-the-scenes founder letter",
  "Priority email support",
  "Everything in Club Premium, included",
];

export const FoundingMemberModal = ({ isOpen, onClose }: Props) => {
  const { toast } = useToast();
  const { isIndian } = useGeoCurrency();
  const { user } = useAuth();

  const [displayName, setDisplayName] = useState(
    user?.user_metadata?.full_name || user?.user_metadata?.name || ""
  );
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [phoneError, setPhoneError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const userEmail = user?.email || "";
  const userName =
    user?.user_metadata?.full_name || user?.user_metadata?.name || userEmail.split("@")[0];

  // INR is base-inclusive of 18% GST at checkout; USD is flat (zero-rated export).
  const foundingGst = isIndian ? applyGst(FOUNDING_INR_BASE) : null;
  const priceLabel = isIndian ? formatInr(foundingGst!.total) : "$999";

  const validate = () => {
    if (!displayName.trim() || displayName.trim().length < 2) {
      toast({ title: "Please enter your display name", variant: "destructive" });
      return false;
    }
    if (isIndian) {
      if (!/^[6-9]\d{9}$/.test(phone)) {
        setPhoneError("Please enter a valid 10-digit Indian mobile number");
        return false;
      }
    } else if (!phone || !isValidPhoneNumber(phone)) {
      setPhoneError("Please enter a valid phone number");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!user) {
      toast({ title: "Please sign in first", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    try {
      const returnUrl = `${window.location.origin}/founding/verify`;
      const { data, error } = await supabase.functions.invoke("create-founding-subscription", {
        body: {
          userName,
          userEmail,
          userPhone: phone,
          returnUrl,
          currency: isIndian ? "INR" : "USD",
          displayName: displayName.trim(),
          city: city.trim() || null,
          country: country.trim() || null,
          countryCode: null,
          isPublic,
        },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = await (error as any)?.context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch {}
        throw new Error(msg);
      }
      if (!data?.success || !data.subscriptionSessionId) {
        throw new Error(data?.error || "Failed to create founding subscription");
      }

      // Persist isPublic preference for the verify page to apply post-activation.
      sessionStorage.setItem(
        "founding-pending",
        JSON.stringify({
          subscriptionId: data.subscriptionId,
          isPublic,
          returnTo: window.location.pathname,
        })
      );

      const { load: loadCashfree } = await import("@cashfreepayments/cashfree-js");
      const cashfree = await loadCashfree({ mode: "production" });
      await cashfree.subscriptionsCheckout({
        subsSessionId: data.subscriptionSessionId,
        returnUrl: `${window.location.origin}/founding/verify?subscriptionId=${encodeURIComponent(
          data.subscriptionId
        )}`,
        redirectTarget: "_self",
      });
    } catch (err) {
      toast({
        title: "Could not start checkout",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  // Stripe flow (international only). Phone not required for Stripe.
  const handleStripeSubmit = async () => {
    if (!user) {
      toast({ title: "Please sign in first", variant: "destructive" });
      return;
    }
    if (!displayName.trim() || displayName.trim().length < 2) {
      toast({ title: "Please enter your display name", variant: "destructive" });
      return;
    }
    setStripeLoading(true);
    try {
      const returnUrl = `${window.location.origin}/founding/verify`;
      const { data, error } = await supabase.functions.invoke("create-stripe-subscription", {
        body: {
          subscriptionType: "founding",
          userName,
          returnUrl,
          displayName: displayName.trim(),
          city: city.trim() || null,
          country: country.trim() || null,
          countryCode: null,
        },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = await (error as any)?.context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch {}
        throw new Error(msg);
      }
      if (!data?.success || !data.checkoutUrl) {
        throw new Error(data?.error || "Could not start Stripe checkout");
      }
      sessionStorage.setItem("founding-pending", JSON.stringify({
        subscriptionId: data.internalSubId,
        isPublic,
        provider: "stripe",
        returnTo: window.location.pathname,
      }));
      window.location.href = data.checkoutUrl;
    } catch (err) {
      toast({
        title: "Stripe Checkout Error",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
      setStripeLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="w-full max-w-lg bg-secondary border border-border rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative p-6 border-b border-border/40 bg-gradient-to-br from-accent/10 to-primary/10">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 text-accent mb-2">
              <Crown className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">
                Founding Member
              </span>
            </div>
            <h2 className="text-2xl font-bold text-foreground">
              Become a Founding Member
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {isIndian && foundingGst
                ? `${formatInr(foundingGst.base)} + 18% GST = ${formatInr(foundingGst.total)}/year, auto-renewing. Your founding-rate price is locked for life.`
                : `${priceLabel}/year, auto-renewing. Your founding-rate price is locked for life.`}
            </p>
            {!isIndian && (
              <div className="mt-3">
                <PaymentProcessorsBadge />
              </div>
            )}
          </div>

          <div className="p-6">
            <ul className="space-y-2 mb-6 text-sm text-foreground/90">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-accent flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="founding-name">Display name (shown on wall)</Label>
                <Input
                  id="founding-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={60}
                  placeholder="Your name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="founding-city">City (optional)</Label>
                  <Input
                    id="founding-city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    maxLength={60}
                    placeholder="Mumbai"
                  />
                </div>
                <div>
                  <Label htmlFor="founding-country">Country (optional)</Label>
                  <Input
                    id="founding-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    maxLength={60}
                    placeholder="India"
                  />
                </div>
              </div>
              <div>
                <Label>Phone</Label>
                {isIndian ? (
                  <Input
                    type="tel"
                    inputMode="numeric"
                    placeholder="10-digit mobile"
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
                      if (phoneError) setPhoneError("");
                    }}
                    maxLength={10}
                  />
                ) : (
                  <IntlPhoneInput
                    value={phone}
                    onChange={(v) => {
                      setPhone(v || "");
                      if (phoneError) setPhoneError("");
                    }}
                    defaultCountry="US"
                    placeholder="Enter phone number with country code"
                    hasError={!!phoneError}
                  />
                )}
                {phoneError && (
                  <p className="text-xs text-destructive mt-1">{phoneError}</p>
                )}
              </div>

              <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
                <Checkbox
                  checked={isPublic}
                  onCheckedChange={(v) => setIsPublic(!!v)}
                  className="mt-0.5"
                />
                <span>List my name publicly on the Founding Members wall.</span>
              </label>

              {isIndian && foundingGst && (
                <div className="bg-muted/50 rounded-xl p-4 border border-border space-y-1.5">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Base price</span>
                    <span className="font-mono text-foreground">{formatInr(foundingGst.base)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">GST (18%)</span>
                    <span className="font-mono text-foreground">{formatInr(foundingGst.gst)}</span>
                  </div>
                  <div className="h-px bg-border my-1.5" />
                  <div className="flex justify-between items-center">
                    <span className="text-foreground font-medium">Total payable</span>
                    <span className="text-xl font-bold text-primary font-mono">
                      {formatInr(foundingGst.total)}
                      <span className="text-xs font-normal text-muted-foreground">/year</span>
                    </span>
                  </div>
                </div>
              )}

              {!isIndian ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleStripeSubmit}
                    disabled={isLoading || stripeLoading}
                    className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold text-[15px] hover:bg-primary/90 transition-colors flex items-center justify-center gap-2.5 disabled:opacity-70"
                  >
                    {stripeLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <span>Pay with</span>
                        <StripeLogo className="h-7 w-auto" />
                        <span className="opacity-80">· {priceLabel}/year</span>
                      </>
                    )}
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading || stripeLoading}
                    className="w-full h-12 rounded-full border border-border bg-transparent text-foreground font-semibold text-[15px] hover:bg-muted/40 transition-colors flex items-center justify-center gap-2.5 disabled:opacity-70"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <span>Pay with</span>
                        <CashfreeLogo className="h-7 w-auto" />
                        <span className="opacity-80">· {priceLabel}/year</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-center text-muted-foreground">
                    Choose whichever processor your card works with. Stripe supports international cards, Apple Pay and Google Pay; Cashfree is an alternate processor. Auto-renews annually. Cancel anytime.
                  </p>
                </div>
              ) : (
                <>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full h-12 rounded-full bg-primary text-primary-foreground font-semibold text-[15px] hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Crown className="w-4 h-4" />
                    )}
                    Continue to checkout · {priceLabel}/year
                  </button>
                  <p className="text-xs text-center text-muted-foreground">
                    Auto-renews annually. Cancel anytime from your dashboard. Permanent badge stays even after cancellation.
                  </p>
                </>
              )}
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default FoundingMemberModal;